import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useRequestMagicLink } from '../api/hooks.js';
import TurnstileGate, { turnstileEnabled } from '../components/TurnstileGate.js';
import { ApiError } from '../api/client.js';
import { formatUnexpectedError } from '../lib/errors.js';

export default function Login() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const request = useRequestMagicLink();

  const needsToken = turnstileEnabled();
  const blocked = needsToken && !turnstileToken;

  return (
    <div className="mx-auto mt-20 max-w-md px-6">
      <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">
        ← Back
      </Link>
      <div className="card mt-4 p-6">
        <h1 className="text-xl font-semibold text-slate-900">Sign in</h1>
        <p className="mt-1 text-sm text-slate-600">
          Enter your email and we'll send you a magic link.
        </p>
        {submitted ? (
          <div className="mt-5 rounded-lg bg-brand-50 p-4 text-sm text-brand-700">
            If <strong>{email}</strong> has an account, a sign-in link has been sent.
          </div>
        ) : (
          <form
            className="mt-5 space-y-3"
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              try {
                await request.mutateAsync({ email, turnstileToken });
                setSubmitted(true);
              } catch (err) {
                if (err instanceof ApiError) {
                  const code = err.code;
                  const friendly = code ? prettyKnownError(code) : null;
                  setError(friendly ?? formatUnexpectedError(err));
                } else {
                  setError(formatUnexpectedError(err));
                }
              }
            }}
          >
            <div>
              <label className="label" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                required
                autoFocus
                className="input mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <TurnstileGate onToken={setTurnstileToken} onError={setError} />
            {error && (
              <p className="rounded-md bg-red-50 p-2 text-sm text-red-700">{error}</p>
            )}
            <button
              type="submit"
              disabled={request.isPending || blocked}
              className="btn-primary w-full"
              title={blocked ? 'Waiting for bot check…' : undefined}
            >
              {request.isPending ? 'Sending…' : 'Send magic link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function prettyKnownError(code: string): string | null {
  switch (code) {
    case 'turnstile_failed':
      return 'Bot check failed. Refresh and try again.';
    case 'rate_limited':
      return 'Too many attempts. Try again in a minute.';
    default:
      return null;
  }
}
